'use client';

import React, { useRef } from 'react';

import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/dist/client/components/navigation';

import { RecapAsset } from '@/api/trading/types';
import { useRecap } from '@/api/trading/useRecap';
import { ApiPlatform } from '@/api/types';
import { Container } from '@/blocks/components/Container';
import { AssetSearch } from '@/blocks/components/Controls/Autocomplete/AssetSearch';
import { useSearchOnChange } from '@/blocks/components/Controls/Autocomplete/helpers';
import { DataGridWrapper, useColumnsVisibilityModel, useRowClick } from '@/blocks/components/DataGrid';
import {
  getColDefCta,
  getColDefHighPrice24h,
  getColDefLowPrice24h,
  getColDefMarket,
  getColDefPriceAndChange,
  getColDefTrend,
  getColDefVol24,
} from '@/blocks/components/DataGrid/Asset/columnsDefenitions';
import { AssetGridRowModel, getAssetRowModel } from '@/blocks/components/DataGrid/Asset/rowModel';
import { EventBlockNames } from '@/providers/analytics/types';

import { getAllAssets, isLocalAsset } from '@/app/[lang]/price-chart/[category]/data';
import { getCategoryBySlug } from '@/app/[lang]/price-chart/[category]/navigation-utils';
import { Asset as LocalAsset } from '@/app/[lang]/price-chart/[category]/types';

import PriceChartCategoriesTabs from './PriceChartCategoriesTabs';
import styles from './styles.module.scss';

function rootHubSorting(assetsCategory: string | null) {
  if (!assetsCategory) {
    return (a: RecapAsset | LocalAsset, b: RecapAsset | LocalAsset) =>
      isLocalAsset(a) || isLocalAsset(b) ? 0 : b.vol24 - a.vol24;
  }
  return () => 0;
}

const supportedFirst = (a: LocalAsset | RecapAsset, b: LocalAsset | RecapAsset) =>
  Number(isLocalAsset(a)) - Number(isLocalAsset(b));

const findMarketsAsset = (markets: RecapAsset[]) => {
  return (a: LocalAsset): RecapAsset | LocalAsset => markets.find((asset) => asset.name === a.name) || a;
};

const byCategory = (category?: string | null) => (a: LocalAsset) =>
  category ? category === a.category : true;

export function PriceChartTableAndControlsSection() {
  const categorySlug = useParams().category as string;
  const tr = useTranslations();
  const assetsCategory = getCategoryBySlug(categorySlug);
  const { markets, loading, error } = useRecap(ApiPlatform.DEVEX);
  const ref = useRef<HTMLDivElement | null>(null);
  const { openCoinPageOnSearchChange } = useSearchOnChange();
  const { openCoinPageOnRowClick } = useRowClick();

  const columnsVisibilityModel = useColumnsVisibilityModel({
    mobile: ['name', 'priceAndChange'],
    tablet: ['name', 'priceAndChange', 'highPrice24h', 'lowPrice24h'],
    desktop: ['name', 'priceAndChange', 'highPrice24h', 'lowPrice24h', 'vol24', 'trendChart', 'cta'],
  });

  if (!markets) {
    return null;
  }

  const columns: GridColDef<AssetGridRowModel>[] = [
    getColDefMarket(tr('pages.priceCharts.root.sections.markets.table.headers.market')),
    getColDefPriceAndChange(tr('pages.priceCharts.root.sections.markets.table.headers.lastPrice')),
    // getColDefPrice(tr('pages.priceCharts.root.sections.markets.table.headers.price')),
    getColDefHighPrice24h(tr('pages.priceCharts.root.sections.markets.table.headers.24hHigh')),
    getColDefLowPrice24h(tr('pages.priceCharts.root.sections.markets.table.headers.24hLow')),
    getColDefVol24(tr('pages.priceCharts.root.sections.markets.table.headers.24hVolume')),
    getColDefTrend(tr('pages.priceCharts.root.sections.markets.table.headers.trend')),
    getColDefCta({
      text: tr('pages.priceCharts.root.sections.markets.table.rowCta'),
      enText: tr('en.pages.priceCharts.root.sections.markets.table.rowCta'),
    }),
  ];

  const filteredMarkets = getAllAssets().filter(byCategory(assetsCategory)).map(findMarketsAsset(markets));

  const rows = filteredMarkets
    .sort(rootHubSorting(assetsCategory))
    .sort(supportedFirst)
    .map((asset) =>
      isLocalAsset(asset)
        ? {
            id: asset.name,
            name: asset.name,
          }
        : getAssetRowModel(asset),
    );

  return (
    <section ref={ref} data-event-block-name={EventBlockNames.assets_table}>
      <Container>
        <div className={styles.stack}>
          <PriceChartCategoriesTabs />
          <AssetSearch
            className={styles.search}
            options={getAllAssets()}
            onChange={openCoinPageOnSearchChange}
          />
          <DataGridWrapper
            loading={loading}
            columns={columns}
            rows={rows}
            onRowClick={openCoinPageOnRowClick}
            columnVisibilityModel={columnsVisibilityModel}
            elementToScrollOnPaginate={ref}
          />
        </div>
      </Container>
    </section>
  );
}
